import axios from 'axios';
import React, {
  createContext,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@material-ui/core';
import { useLocation, useNavigate } from 'react-router-dom';
import various, { frontSideRequireAuth } from './General/various';
import { myPage } from './Apis/apiOfManaged';
import { permissionCheck } from './Apis/apis';

export const AuthContext = createContext();

const AUTH_RECOVERY_ATTEMPT_KEY = 'touzen_auth_recovery_attempted_v1';
const AUTH_REQUEST_TIMEOUT_MS = 15000;
const AUTH_USER_SNAPSHOT_KEY = 'touzen_auth_user_snapshot_v1';
const AUTH_SUMMARY_SNAPSHOT_KEY = 'touzen_auth_summary_v1';

const parseStoredPermissions = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(various.permissionKey);
    if (!raw || raw === 'null') {
      return [];
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const readStoredUserSnapshot = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(AUTH_USER_SNAPSHOT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const readStoredAuthSummary = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(AUTH_SUMMARY_SNAPSHOT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const writeStoredUserSnapshot = (user) => {
  if (typeof window === 'undefined' || !user) {
    return;
  }

  window.localStorage.setItem(AUTH_USER_SNAPSHOT_KEY, JSON.stringify(user));
};

const writeStoredAuthSummary = (authSummary) => {
  if (typeof window === 'undefined' || !authSummary) {
    return;
  }

  window.localStorage.setItem(AUTH_SUMMARY_SNAPSHOT_KEY, JSON.stringify(authSummary));
};

const extractTouzenPermissionsFromAuthSummary = (authSummary = null) => {
  const touzenPayload = authSummary?.domain_payload?.touzen || authSummary?.domainPayload?.touzen || {};
  const permissions = touzenPayload.permissions || authSummary?.permissions || [];

  return Array.isArray(permissions) ? permissions.filter(Boolean) : [];
};

const buildTouzenUserSnapshotFromAuthSummary = (authSummary = null) => {
  const subject = authSummary?.subject || {};
  const email = subject.email || authSummary?.email || '';

  if (!email) {
    return null;
  }

  return {
    email,
    id: subject.id || subject.uuid || authSummary?.subject_id || '',
    display_name: subject.display_name || subject.displayName || '',
  };
};

export const applyTouzenAuthSummary = (authSummary = null) => {
  if (typeof window === 'undefined' || !authSummary) {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(AUTH_USER_SNAPSHOT_KEY);
      window.localStorage.removeItem(AUTH_SUMMARY_SNAPSHOT_KEY);
      window.localStorage.setItem(various.permissionKey, null);
    }
    return {
      applied: false,
      permissions: [],
      user: null,
    };
  }

  writeStoredAuthSummary(authSummary);

  const permissions = extractTouzenPermissionsFromAuthSummary(authSummary);
  window.localStorage.setItem(various.permissionKey, JSON.stringify(permissions));

  const user = buildTouzenUserSnapshotFromAuthSummary(authSummary);
  if (user) {
    writeStoredUserSnapshot(user);
  } else window.localStorage.removeItem(AUTH_USER_SNAPSHOT_KEY);

  return {
    applied: true,
    permissions,
    user,
  };
};

const fetchTouzenAuthSummary = async () => {
  const response = await axios.get(`${various.currentDomain}api/auth/summary/`, {
    timeout: AUTH_REQUEST_TIMEOUT_MS,
  });

  return response.data?.auth_summary || response.data;
};

export const clearStoredAuthSession = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem('Authorization');
  window.localStorage.removeItem('accesstoken');
  window.localStorage.removeItem('refreshtoken');
  window.localStorage.removeItem('permission');
  window.localStorage.removeItem(AUTH_USER_SNAPSHOT_KEY);
  window.localStorage.removeItem(AUTH_SUMMARY_SNAPSHOT_KEY);
  window.localStorage.setItem(various.permissionKey, null);
};

export const readStoredAuthSnapshot = () => {
  if (typeof window === 'undefined') {
    return {
      isAuthenticated: false,
      accessToken: null,
      refreshToken: null,
      permissions: [],
      canAccessAdmin: false,
    };
  }

  const permissions = parseStoredPermissions();
  const authSummary = readStoredAuthSummary();
  const commonFlags = authSummary?.common_flags || authSummary?.commonFlags || {};

  const accessToken = window.localStorage.getItem('accesstoken');
  const refreshToken = window.localStorage.getItem('refreshtoken');
  const canAccessAdmin =
    commonFlags.can_access_admin === true ||
    commonFlags.canAccessAdmin === true ||
    permissions.includes('admin');

  return {
    isAuthenticated: Boolean(window.localStorage.getItem('Authorization') && accessToken && refreshToken),
    accessToken,
    refreshToken,
    permissions,
    canAccessAdmin,
    canViewMyPage: commonFlags.can_view_mypage ?? commonFlags.canViewMyPage ?? null,
    authSummary,
  };
};

const normalizeAuthPath = (path) => {
  if (!path || path === '/') {
    return '/';
  }
  return path.replace(/\/+$/, '') || '/';
};

const publicAuthEndpointFragments = [
  'api/auth/jwt/create/',
  'api/auth/jwt/refresh/',
];
const protectedAuthEndpointFragments = [
  'api/auth/users/',
  'api/auth/summary/',
];

const isPublicAuthEndpoint = (url = '') => {
  const requestUrl = String(url);
  return publicAuthEndpointFragments.some((fragment) => requestUrl.indexOf(fragment) !== -1);
};

const shouldAttachAuthHeader = (url = '') => {
  const requestUrl = String(url);
  if (isPublicAuthEndpoint(requestUrl)) {
    return false;
  }

  return (
    requestUrl.indexOf(various.apiUrlsRequireAuth) !== -1 ||
    protectedAuthEndpointFragments.some((fragment) => requestUrl.indexOf(fragment) !== -1)
  );
};

const isAuthStatusFailure = (result) =>
  result?.error && (result.status === 401 || result.status === 403);

const AuthContextProvider = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authSnapshot, setAuthSnapshot] = useState(readStoredAuthSnapshot);
  const currentAuthPath = normalizeAuthPath(location.pathname);
  const routeRequiresAuth = frontSideRequireAuth()
    .map(normalizeAuthPath)
    .includes(currentAuthPath);
  const isAuth = authSnapshot.isAuthenticated;
  const permissions = isAuth ? authSnapshot.permissions : [];
  const canAccessAdmin = isAuth && authSnapshot.canAccessAdmin;
  const canViewMyPage = isAuth && authSnapshot.canViewMyPage !== false;
  const [user, setUser] = useState(null);
  // eslint-disable-next-line
  const [checked, setChecked] = useState(false);
  const [authReady, setAuthReady] = useState(!routeRequiresAuth || !isAuth);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [interceptorsReady, setInterceptorsReady] = useState(false);
  const sessionGeneration = useRef(0);
  const requestInterceptorRef = useRef(null);
  const responseInterceptorRef = useRef(null);
  const refreshPromiseRef = useRef(null);

  const clearRecoveryAttempt = useCallback(() => {
    window.sessionStorage.removeItem(AUTH_RECOVERY_ATTEMPT_KEY);
  }, []);

  const hasRecoveryAttempt = useCallback(() => {
    return window.sessionStorage.getItem(AUTH_RECOVERY_ATTEMPT_KEY) === '1';
  }, []);

  const markRecoveryAttempt = useCallback(() => {
    window.sessionStorage.setItem(AUTH_RECOVERY_ATTEMPT_KEY, '1');
  }, []);

  const logAuthEvent = useCallback((event, detail = {}) => {
    console.log('[auth]', event, {
      path: location.pathname,
      ...detail,
    });
  }, [location.pathname]);

  const login = useCallback(() => {
    sessionGeneration.current += 1;
    refreshPromiseRef.current = null;
    window.localStorage.setItem('Authorization', true);
    clearRecoveryAttempt();
    setAuthSnapshot(readStoredAuthSnapshot());
    setUser(readStoredUserSnapshot());
    console.log('login...');
  }, [clearRecoveryAttempt]);

  const logout = useCallback(() => {
    sessionGeneration.current += 1;
    refreshPromiseRef.current = null;
    clearStoredAuthSession();
    clearRecoveryAttempt();
    setAuthSnapshot(readStoredAuthSnapshot());
    setUser(null);
    setAuthReady(true);
    console.log('logout...');
  }, [clearRecoveryAttempt]);

  const refreshTokens = useCallback(async () => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const refresh = window.localStorage.getItem('refreshtoken');
    const generation = sessionGeneration.current;
    if (!refresh) {
      throw new Error('refresh token is missing');
    }

    const request = axios
      .post(`${various.currentDomain}api/auth/jwt/refresh/`, {
        refresh,
      }, {
        timeout: AUTH_REQUEST_TIMEOUT_MS,
      })
      .then((res) => {
        if (generation !== sessionGeneration.current || window.localStorage.getItem('refreshtoken') !== refresh) {
          throw new Error('authentication session changed');
        }
        if (!res.data.access) throw new Error('refresh response has no access token');
        if (res.data.access) {
          window.localStorage.setItem('accesstoken', res.data.access);
        }
        if (res.data.refresh) {
          window.localStorage.setItem('refreshtoken', res.data.refresh);
        }
        clearRecoveryAttempt();
        return res;
      })
      .finally(() => {
        if (refreshPromiseRef.current === request) refreshPromiseRef.current = null;
      });
    refreshPromiseRef.current = request;
    return request;
  }, [clearRecoveryAttempt]);

  const openReloginDialog = useCallback(() => {
    logout();
    setUser(null);
    setAuthReady(true);
    setAuthDialogOpen(true);
  }, [logout]);

  const handleAuthDialogClose = useCallback(() => {
    setAuthDialogOpen(false);
    navigate(various.general.login, {
      state: { previous: location.pathname },
      replace: true,
    });
  }, [location.pathname, navigate]);

  const isCurrentRouteAuthRequired = useCallback(
    () => routeRequiresAuth,
    [routeRequiresAuth]
  );

  const handleRecoveryFailure = useCallback((event, error) => {
    const hasAuthorization = Boolean(window.localStorage.getItem('Authorization'));
    const hasRefreshToken = Boolean(window.localStorage.getItem('refreshtoken'));
    const isStaleAuthState = hasAuthorization && hasRefreshToken;
    const alreadyAttempted = hasRecoveryAttempt();
    const authRequired = isCurrentRouteAuthRequired();

    logAuthEvent(event, {
      authRequired,
      isStaleAuthState,
      alreadyAttempted,
      message: error?.message,
    });

    if (!authRequired) {
      logout();
      setUser(null);
      setAuthReady(true);
      setAuthDialogOpen(false);
      return 'public-cleared';
    }

    if (isStaleAuthState && !alreadyAttempted) {
      markRecoveryAttempt();
      setAuthReady(true);
      navigate(0);
      return 'recovery';
    }

    openReloginDialog();
    return 'relogin';
  }, [
    hasRecoveryAttempt,
    isCurrentRouteAuthRequired,
    logAuthEvent,
    logout,
    markRecoveryAttempt,
    navigate,
    openReloginDialog,
  ]);

  useLayoutEffect(() => {
    requestInterceptorRef.current = axios.interceptors.request.use(
      async (config) => {
        if (shouldAttachAuthHeader(config.url)) {
          config._touzenAuthGeneration = sessionGeneration.current;
          let accessToken = window.localStorage.getItem('accesstoken');
          const refreshToken = window.localStorage.getItem('refreshtoken');

          config.headers = config.headers || {};
          if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
            // Axios must preserve the multipart body; the browser supplies its boundary.
            Object.keys(config.headers).forEach((name) => {
              if (name.toLowerCase() === 'content-type') delete config.headers[name];
            });
          } else {
            config.headers['Content-Type'] = 'application/json; charset=utf-8';
          }

          if (!accessToken && refreshToken) {
            try {
              const refreshResponse = await refreshTokens();
              accessToken =
                refreshResponse?.data?.access ||
                window.localStorage.getItem('accesstoken');
            } catch (refreshError) {
              if (config._touzenAuthGeneration === sessionGeneration.current) {
                handleRecoveryFailure('request-refresh-failed', refreshError);
              }
              return Promise.reject(refreshError);
            }
          }

          if (accessToken) {
            if (config._touzenAuthGeneration !== sessionGeneration.current) {
              return Promise.reject(new Error('authentication session changed'));
            }
            config.headers['Authorization'] = `Bearer ${accessToken}`;
          } else {
            return Promise.reject(
              new Error('authorization token is missing for protected request')
            );
          }

        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    responseInterceptorRef.current = axios.interceptors.response.use(
      (response) => {
        if (response.config?._touzenAuthGeneration !== undefined && response.config._touzenAuthGeneration !== sessionGeneration.current) {
          return Promise.reject(new Error('authentication session changed'));
        }
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        if (originalRequest?._touzenAuthGeneration !== undefined && originalRequest._touzenAuthGeneration !== sessionGeneration.current) {
          return Promise.reject(error);
        }
        const refreshToken = window.localStorage.getItem('refreshtoken');
        const isRefreshRequest =
          originalRequest &&
          originalRequest.url &&
          originalRequest.url.indexOf('api/auth/jwt/refresh/') !== -1;
        const isPublicAuthRequest =
          originalRequest && isPublicAuthEndpoint(originalRequest.url);

        if (
          error.response &&
          error.response.status === 401 &&
          originalRequest &&
          !originalRequest._retry &&
          refreshToken &&
          !isRefreshRequest &&
          !isPublicAuthRequest
        ) {
          originalRequest._retry = true;
          try {
            console.log('Refresh successfuly in retry');
            const refreshResponse = await refreshTokens();
            const accessToken =
              refreshResponse?.data?.access ||
              window.localStorage.getItem('accesstoken');
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            if (originalRequest._touzenAuthGeneration === sessionGeneration.current) {
              handleRecoveryFailure('retry-refresh-failed', refreshError);
            }
            return Promise.reject(refreshError);
          }
        }

        if (error.response?.status === 401 && originalRequest && shouldAttachAuthHeader(originalRequest.url)) {
          handleRecoveryFailure('protected-request-unauthorized', error);
        }

        return Promise.reject(error);
      }
    );
    // Register before child passive effects, including after route changes.
    setInterceptorsReady(true);

    return () => {
      axios.interceptors.request.eject(requestInterceptorRef.current);
      axios.interceptors.response.eject(responseInterceptorRef.current);
    };
  }, [handleRecoveryFailure, refreshTokens]);

  useEffect(() => {
    let active = true;
    const generation = sessionGeneration.current;

    if (!isCurrentRouteAuthRequired()) {
      const cachedUser = isAuth ? readStoredUserSnapshot() : null;
      setUser(cachedUser);
      setAuthReady(true);
      if (!isAuth || cachedUser) return () => { active = false; };
    }

    if (!isAuth) {
      setUser(null);
      window.localStorage.setItem(various.permissionKey, null);
      setAuthReady(true);
      return () => {
        active = false;
      };
    }

    const bootstrapAuth = async () => {
      setAuthReady(false);
      const cachedPermissions = parseStoredPermissions();
      const canUseCachedPermissions = cachedPermissions.length > 0;

      try {
        const authSummary = await fetchTouzenAuthSummary();
        if (!active || generation !== sessionGeneration.current) {
          return;
        }

        const summaryResult = applyTouzenAuthSummary(authSummary);
        setAuthSnapshot(readStoredAuthSnapshot());
        if (summaryResult.user && (summaryResult.permissions.length > 0 || !isCurrentRouteAuthRequired())) {
          setUser(summaryResult.user);
          setChecked(true);
          setAuthReady(true);
          return;
        }

        logAuthEvent('auth-summary-insufficient', {
          hasUser: Boolean(summaryResult.user),
          permissionsCount: summaryResult.permissions.length,
        });
      } catch (summaryError) {
        logAuthEvent('auth-summary-unavailable', {
          message: summaryError?.message,
        });
      }

      if (!active || generation !== sessionGeneration.current) {
        return;
      }

      console.log('myPage');
      const currentUserResult = await myPage();
      if (!active || generation !== sessionGeneration.current) {
        return;
      }

      if (isAuthStatusFailure(currentUserResult)) {
        handleRecoveryFailure(
          'current-user-unavailable',
          new Error('current user is unavailable')
        );
        return;
      }

      if (currentUserResult?.error) {
        const cachedUser = readStoredUserSnapshot();
        logAuthEvent('current-user-temporary-unavailable', {
          status: currentUserResult.status,
          code: currentUserResult.code,
          message: currentUserResult.message,
          canUseCachedPermissions,
          hasCachedUser: Boolean(cachedUser),
        });

        if (cachedUser && canUseCachedPermissions) {
          setUser(cachedUser);
          setAuthReady(true);
          return;
        }

        setAuthReady(true);
        return;
      }

      const currentUser = currentUserResult;
      setUser(currentUser);
      writeStoredUserSnapshot(currentUser);

      // Public pages need the verified identity, without a permission prerequisite.
      if (!isCurrentRouteAuthRequired()) {
        setAuthReady(true);
        return;
      }

      if (canUseCachedPermissions && active) {
        setAuthReady(true);
      }

      if (currentUser.email) {
        console.log('permissionCheck');
        const permissionResult = await permissionCheck(
          { email: currentUser.email },
          setChecked
        );
        if (!active || generation !== sessionGeneration.current) return;
        if (!permissionResult && !canUseCachedPermissions) {
          handleRecoveryFailure(
            'permission-check-unavailable',
            new Error('permission check is unavailable')
          );
          return;
        }
      }

      if (active) {
        setAuthReady(true);
      }
    };

    bootstrapAuth();

    return () => {
      active = false;
    };
  }, [authSnapshot.accessToken, handleRecoveryFailure, isAuth, isCurrentRouteAuthRequired, logAuthEvent]);

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        isAuth,
        authReady,
        user,
        permissions,
        canAccessAdmin,
        canViewMyPage,
      }}
    >
      {interceptorsReady && (!routeRequiresAuth || authReady) && !authDialogOpen ? props.children : null}
      <Dialog
        open={authDialogOpen}
        onClose={handleAuthDialogClose}
        aria-labelledby="auth-relogin-dialog-title"
        aria-describedby="auth-relogin-dialog-description"
      >
        <DialogTitle id="auth-relogin-dialog-title">
          再ログインのお願い
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="auth-relogin-dialog-description">
            セッションの再確認に失敗しました。お手数ですが、もう一度ログインしてください。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAuthDialogClose} color="primary">
            ログイン画面へ
          </Button>
        </DialogActions>
      </Dialog>
    </AuthContext.Provider>
  );
};
export default AuthContextProvider;
